/**
 * StageQuickPicker Component
 * 
 * Quick stage change modal accessed via Command Palette.
 * 
 * Architecture:
 * - Uses dynamic stages from CrmSettingsContext (Supabase)
 * - Writes stage changes to Supabase crm_deals table
 * - Logs activity to Supabase crm_activities table
 */

import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  SimpleGrid,
  Button,
  Text,
  useToast,
  Box,
  Alert,
  AlertIcon,
  Spinner,
  Center,
} from '@chakra-ui/react';
import { doc, getDoc } from 'firebase/firestore';
import { db, functionsUsEast1 } from '../../../utils/init-firebase';
import { httpsCallable } from 'firebase/functions';
import { supabase, isSupabaseConfigured } from '../../../utils/supabase';
import { useCrmSettings } from '../../../contexts/CrmSettingsContext';
import { useAuth } from '../../../contexts/AuthContext';

export default function StageQuickPicker({ isOpen, onClose, deal, onStageChanged }) {
  const [updating, setUpdating] = useState(false);
  const [workflow, setWorkflow] = useState(null);
  const toast = useToast();
  const { currentUser } = useAuth();
  const { dealStages, stagesLoading, getStagesForPicker } = useCrmSettings();

  useEffect(() => {
    if (isOpen) {
      loadWorkflow();
    }
  }, [isOpen]);

  const loadWorkflow = async () => {
    try {
      // Workflow config is still in Firestore for now
      const workflowDoc = await getDoc(doc(db, 'config', 'stageTransitions'));
      if (workflowDoc.exists()) {
        setWorkflow(workflowDoc.data());
      } else {
        // Use default if no config exists
        setWorkflow({
          transitions: {},
          allowBackwards: false,
          allowSkipping: true,
        });
      }
    } catch (error) {
      console.error('Error loading workflow:', error);
      setWorkflow({ transitions: {}, allowBackwards: true, allowSkipping: true });
    }
  };

  const isTransitionAllowed = (fromStage, toStage) => {
    if (!workflow || !workflow.transitions) {
      return true; // Allow all if no workflow configured
    }

    const allowedTransitions = workflow.transitions[fromStage] || [];
    
    // Always allow moving to lost
    if (toStage === 'deal-lost') {
      return true;
    }

    return allowedTransitions.includes(toStage);
  };

  const getStageName = (stageValue) => {
    return dealStages.find(s => s.value === stageValue)?.label || stageValue;
  };

  const handleStageChange = async (newStage) => {
    if (!deal) return;

    // Validate transition
    if (!isTransitionAllowed(deal.stage, newStage)) {
      const fromStageName = getStageName(deal.stage);
      const toStageName = getStageName(newStage);
      
      toast({
        title: 'Invalid stage transition',
        description: `Cannot move from "${fromStageName}" to "${toStageName}". Please follow the workflow.`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    
    setUpdating(true);
    try {
      if (!isSupabaseConfigured()) {
        throw new Error('Supabase is not configured');
      }

      const oldStage = deal.stage;
      const newStageName = getStageName(newStage);

      // Update deal stage in Supabase
      const { error: updateError } = await supabase
        .from('crm_deals')
        .update({
          stage: newStage,
          updated_at: new Date().toISOString(),
        })
        .eq('id', deal.id);

      if (updateError) throw updateError;

      try {
        httpsCallable(functionsUsEast1, 'handleAutomationTriggers')({
          eventType: 'deal.stage_changed',
          payload: { contactId: deal.contactId, dealId: deal.id, fromStage: oldStage, toStage: newStage },
        });
      } catch (_) {}

      // Log activity to Supabase
      const { error: activityError } = await supabase
        .from('crm_activities')
        .insert({
          deal_id: deal.id,
          type: 'stage_change',
          description: `Stage changed from ${getStageName(oldStage)} to ${newStageName}`,
          created_by: currentUser.uid,
          metadata: {
            old_stage: oldStage,
            new_stage: newStage,
          },
        });

      if (activityError) {
        console.warn('Failed to log activity:', activityError);
        // Don't throw - stage update succeeded
      }

      toast({
        title: 'Stage updated',
        description: `Changed to ${newStageName}`,
        status: 'success',
        duration: 2000,
      });

      // Callback to notify parent of change
      if (onStageChanged) {
        onStageChanged(newStage);
      }

      onClose();
    } catch (error) {
      console.error('Error updating stage:', error);
      toast({
        title: 'Error updating stage',
        description: error.message,
        status: 'error',
        duration: 3000,
      });
    } finally {
      setUpdating(false);
    }
  };

  // Show loading while stages are being fetched
  if (stagesLoading) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent>
          <ModalHeader>Quick Stage Change</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Center py={8}>
              <Spinner />
            </Center>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent>
        <ModalHeader>Quick Stage Change</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Text fontSize="sm" color="gray.600" mb={4}>
            Select new stage for: <strong>{deal?.dealName}</strong>
          </Text>
          {workflow && !isTransitionAllowed(deal?.stage, deal?.stage) && (
            <Alert status="info" size="sm" mb={3}>
              <AlertIcon />
              <Text fontSize="xs">
                Some transitions are restricted by workflow rules. Disabled options cannot be selected.
              </Text>
            </Alert>
          )}
          
          <SimpleGrid columns={2} spacing={3}>
            {getStagesForPicker(deal?.stage).map((stage) => {
              const isCurrent = deal?.stage === stage.value;
              const isAllowed = isCurrent || isTransitionAllowed(deal?.stage, stage.value);
              
              return (
                <Button
                  key={stage.value}
                  colorScheme={stage.color}
                  variant={isCurrent ? 'solid' : 'outline'}
                  onClick={() => handleStageChange(stage.value)}
                  isLoading={updating}
                  isDisabled={isCurrent || !isAllowed}
                  size="md"
                  height="60px"
                  whiteSpace="normal"
                  textAlign="center"
                  opacity={isAllowed ? 1 : 0.4}
                >
                  <Box>
                    <Text fontSize="sm" fontWeight="semibold">
                      {stage.label}
                    </Text>
                    {isCurrent && (
                      <Text fontSize="xs" mt={1}>
                        (Current)
                      </Text>
                    )}
                    {!isAllowed && !isCurrent && (
                      <Text fontSize="xs" mt={1}>
                        (Restricted)
                      </Text>
                    )}
                  </Box>
                </Button>
              );
            })}
          </SimpleGrid>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
